import { DateTime } from 'luxon';

import ResultsOverall, { IResultsOverallAPI } from '@/common/models/lift-dashboard/ResultsOverall';
import ResultsPerCity, { IResultPerCityAPI } from '@/common/models/lift-dashboard/ResultsPerCity';
import ResultsPerCreative, { IResultPerCreativeAPI } from '@/common/models/lift-dashboard/ResultsPerCreative';
import ResultsPerMarket, { IResultPerMarketAPI } from '@/common/models/lift-dashboard/ResultsPerMarket';
import ResultsPerTarget, { IResultPerTargetAPI } from '@/common/models/lift-dashboard/ResultsPerTarget';
import ResultsPerUnit, { IResultPerUnitAPI } from '@/common/models/lift-dashboard/ResultsPerUnit';
import { isNil } from '@/globals';
import Validatable from '@/validator/Validatable';

export interface Geofence {
  created_on_utc: string;
  description: string;
  geojson: {
    coordinates: [number, number][];
    type: string;
  };
  hivestack_id: number;
  id: number;
  latitude: number;
  longitude: number;
  modified_on_utc: string;
  name: string;
  swarm_aws_region: string;
}

export interface ILiftAnalysisDataAPI {
  id: number;
  name: string;
  results_overall: IResultsOverallAPI;
  results_per_day: never[];
  results_per_target: IResultPerTargetAPI[];
  results_per_unit: IResultPerUnitAPI[];
  results_per_creative: IResultPerCreativeAPI[];
  results_per_market: IResultPerMarketAPI[];
  results_per_location: IResultPerCityAPI[];
  workflowexecution_id: number;
  created_on_utc: string;
}

export default class LiftAnalysisData extends Validatable {
  id: number;

  name: string;

  resultsOverall: ResultsOverall;

  resultsPerTarget: ResultsPerTarget[];

  resultsPerUnit: ResultsPerUnit[];

  resultsPerCreative: ResultsPerCreative[];

  resultsPerMarket: ResultsPerMarket[];

  resultsPerCity: ResultsPerCity[];

  workflowExecutionID: number;

  createdOnUtc: DateTime;

  constructor(
    id: number,
    name: string,
    resultsOverall: ResultsOverall,
    resultsPerDay: never[],
    resultsPerTarget: ResultsPerTarget[],
    resultsPerUnit: ResultsPerUnit[],
    resultsPerCreative: ResultsPerCreative[],
    resultsPerMarket: ResultsPerMarket[],
    resultsPerCity: ResultsPerCity[],
    workflowExecutionID: number,
    createdOnUtc: DateTime,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.resultsOverall = resultsOverall;
    this.resultsPerDay = resultsPerDay;
    this.resultsPerTarget = resultsPerTarget;
    this.resultsPerUnit = resultsPerUnit;
    this.resultsPerCreative = resultsPerCreative;
    this.resultsPerMarket = resultsPerMarket;
    this.resultsPerCity = resultsPerCity;
    this.workflowExecutionID = workflowExecutionID;
    this.createdOnUtc = createdOnUtc;
  }

  get description(): string {
    return `${this.createdOnFormatted} – ${this.name}`;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_SHORT);
  }

  static fromApi(apiObject: ILiftAnalysisDataAPI): LiftAnalysisData {
    return new LiftAnalysisData(
      apiObject.id,
      apiObject.name,
      ResultsOverall.fromApi(apiObject.results_overall),
      apiObject.results_per_day,
      apiObject.results_per_target.map((result) => ResultsPerTarget.fromApi(result)),
      apiObject.results_per_unit.map((result) => ResultsPerUnit.fromApi(result)),
      apiObject.results_per_creative?.map((result) => ResultsPerCreative.fromApi(result)) ?? [],
      apiObject.results_per_market?.map((result) => ResultsPerMarket.fromApi(result)) ?? [],
      apiObject.results_per_location?.map((result) => ResultsPerCity.fromApi(result)) ?? [],
      apiObject.workflowexecution_id,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
    );
  }

  static fromObject(obj: LiftAnalysisData) {
    return super.fromObject(
      obj,
      new LiftAnalysisData(
        obj.id,
        obj.name,
        obj.resultsOverall,
        obj.resultsPerDay,
        obj.resultsPerTarget,
        obj.resultsPerUnit,
        obj.resultsPerCreative,
        obj.resultsPerMarket,
        obj.resultsPerCity,
        obj.workflowExecutionID,
        obj.createdOnUtc,
      ),
    );
  }

  toApi(): ILiftAnalysisDataAPI {
    return {
      id: this.id,
      name: this.name,
      results_overall: this.resultsOverall?.toApi(),
      results_per_day: this.resultsPerDay,
      results_per_target: this.resultsPerTarget?.map((result) => result.toApi()),
      results_per_unit: this.resultsPerUnit?.map((result) => result.toApi()),
      results_per_creative: this.resultsPerCreative?.map((result) => result.toApi()),
      results_per_market: this.resultsPerMarket?.map((result) => result.toApi()),
      results_per_location: this.resultsPerCity?.map((result) => result.toApi()),
      workflowexecution_id: this.workflowExecutionID,
      created_on_utc: this.createdOnUtc?.toJSDate().toISOString(),
    };
  }

  //
  // Lift v2
  // if those keys are all set to -1, it means we are not using lift v2 and we need to use the old keys
  //   'control_group_total_pre_campaign',
  //   'control_group_visited_pre_campaign',
  //   'exposed_to_ad_group_total_pre_campaign',
  //   'exposed_to_ad_group_visited_pre_campaign',
  //
  get isLiftV2(): boolean {
    const liftV2Keys = [
      'controlGroupTotalPreCampaign',
      'controlGroupVisitedPreCampaign',
      'exposedToAdGroupTotalPreCampaign',
      'exposedToAdGroupVisitedPreCampaign',
    ];
    if (!this.resultsOverall) return false;
    const preCampaignValues = liftV2Keys.map((key) => this.resultsOverall[key]);
    return preCampaignValues.filter((v) => v !== -1 && !isNil(v)).length > 0;
  }

  static validatorCreateRules() {
    return {};
  }
}
